import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const VirtualAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm the Paradise Landscaping virtual assistant. How can I help you today?",
      sender: "assistant"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const { currentUser } = useAuth();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Close chat when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatContainerRef.current && !chatContainerRef.current.contains(event.target) && 
          !event.target.classList.contains('assistant-toggle-btn')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      text: inputMessage.trim(),
      sender: "user"
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call API to get response
      const response = await getAssistantResponse(inputMessage.trim());
      
      // Add assistant response to chat
      const assistantMessage = {
        id: Date.now() + 1,
        text: response,
        sender: "assistant"
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error getting assistant response:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        text: "Lo siento, tuve un problema al procesar tu solicitud. Por favor, intenta nuevamente.",
        sender: "assistant"
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const getAssistantResponse = async (userMessage) => {
    // Check if user wants to book a service
    const bookingKeywords = ['book', 'schedule', 'appointment', 'reserve', 'booking', 'reservar', 'cita', 'agendar'];
    const isBookingRequest = bookingKeywords.some(keyword => 
      userMessage.toLowerCase().includes(keyword)
    );
    
    if (isBookingRequest) {
      // Redirect to appointments page after a short delay
      setTimeout(() => {
        window.location.href = '/appointments';
      }, 2000);
      
      return "I'll help you book a service! I'm redirecting you to our appointments page where you can schedule your service...";
    }
    
    // Regular API call for other queries
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: `You are a virtual assistant for Paradise Landscaping, a landscaping and snow removal company in Northeast Ohio.
              
              Company information:
              - Services: lawn maintenance, spring and fall cleanups, mulch installation, concrete & paver patios, retaining walls, flowerbed planting, and snow plowing.
              - Experience: over 20 years serving homes and businesses.
              - Values: family-owned, reliable, always available, customized solutions, fast response, residential & commercial services, eco-friendly practices.
              
              Your goal is to help customers with information about services, schedule free estimates, answer common questions, and be friendly and professional.
              
              Always respond in English unless the user specifically asks for Spanish. If the user wants to book a service, tell them you'll redirect them to the appointments page.`
            },
            {
              role: "user",
              content: userMessage
            }
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error.message);
      }
      
      return data.choices[0].message.content;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button 
        className="assistant-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#2e7d32',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1000,
          transition: 'all 0.3s ease'
        }}
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {/* Chat popup */}
      {isOpen && (
        <div 
          ref={chatContainerRef}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            width: '350px',
            maxWidth: 'calc(100vw - 40px)',
            height: '500px',
            maxHeight: 'calc(100vh - 120px)',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 5px 25px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1000,
            animation: 'fadeIn 0.3s ease'
          }}
        >
          {/* Header */}
          <div style={{
            backgroundColor: '#2e7d32',
            color: 'white',
            padding: '15px 20px',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '16px' }}>Paradise Landscaping</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Virtual Assistant</div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>

          {/* Messages area */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            backgroundColor: '#f5f5f5'
          }}>
            {messages.map(message => (
              <div 
                key={message.id}
                style={{
                  alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  backgroundColor: message.sender === 'user' ? '#e1ffc7' : 'white',
                  padding: '10px 15px',
                  borderRadius: message.sender === 'user' ? '18px 18px 0 18px' : '18px 18px 18px 0',
                  maxWidth: '80%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  wordBreak: 'break-word'
                }}
              >
                {message.text}
              </div>
            ))}
            {isTyping && (
              <div style={{
                alignSelf: 'flex-start',
                backgroundColor: 'white',
                padding: '10px 15px',
                borderRadius: '18px 18px 18px 0',
                maxWidth: '80%',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={{
            padding: '15px',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '10px'
          }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '10px 15px',
                borderRadius: '20px',
                border: '1px solid #ddd',
                outline: 'none'
              }}
              disabled={isTyping}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping}
              style={{
                backgroundColor: '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputMessage.trim() && !isTyping ? 'pointer' : 'not-allowed',
                opacity: inputMessage.trim() && !isTyping ? 1 : 0.7
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* CSS for typing indicator */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #aaa;
          display: inline-block;
          animation: bounce 1.5s infinite ease-in-out;
        }
        
        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }
        
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        
        @keyframes bounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </>
  );
};

export default VirtualAssistant;
